import { USER_ROLES } from "../../interfaces/roles";
import { user } from "../actions/actionContants";
import storage from "../../utils/storage";
import JwtDecode from "jwt-decode";
import { IUser } from "../../interfaces/user";
import { DeepPartial } from "redux";
import { ActionType } from "../actions/action";
import { IPreference } from "../../interfaces/preference";
import { IBrand } from "../../interfaces/company";
import { ViewMode } from "../actions/user/userAction";
import { isSubscriptionExpired, getDefaultViewMode } from "../../utils/subscription";
import { IUserCredits, IUserAccessPermissions } from "../../interfaces/user";

export enum AuthState {
	SIGNED_IN = "SIGNED_IN",
	TWO_FACTOR_AUTH = "TWO_FACTOR_AUTH",
	SIGNED_OUT = "SIGNED_OUT"
}

export interface IUserState {
	email: string | null;
	role: string;
	user_id: string | null;
	isLoggedIn: boolean;
	userPreferences: IPreference[];
	profile?: IUser;
	users: IUser[];
	selected_brand: IBrand;
	auth_state: AuthState;
	currentViewMode: ViewMode;
	credits?: IUserCredits;
	permissions?: IUserAccessPermissions;
	loadingCredits: boolean;
	loadingPermissions: boolean;
}

// Get view mode from localStorage or use default based on profile
const getInitialViewMode = (profile?: IUser): ViewMode => {
	const savedMode = storage.getItem("viewMode") as ViewMode | null;

	// If user has a saved preference, use it
	if (savedMode === "supplier" || savedMode === "manufacturer") {
		return savedMode;
	}

	// If no saved preference, use default based on subscription type
	if (profile) {
		const defaultMode = getDefaultViewMode(profile);
		// Save the default mode to localStorage
		storage.setItem("viewMode", defaultMode);
		return defaultMode;
	}

	// Fallback to manufacturer if no profile available
	return "manufacturer";
};

const INIT_STATE: IUserState = {
	email: "",
	role: "",
	user_id: "",
	isLoggedIn: false,
	userPreferences: [],
	users: [],
	selected_brand: {
		id: ""
	},
	auth_state: AuthState.SIGNED_OUT,
	currentViewMode: getInitialViewMode(),
	loadingCredits: false,
	loadingPermissions: false
};

function getAuthState(): IUserState {
	const token = storage.getItem("access_token");

	if (!token) return INIT_STATE;

	try {
		const user = JwtDecode(token) as any;
		return {
			email: user.email,
			role: user.claims.role,
			isLoggedIn: !!user.claims.app_user_id,
			user_id: user.claims.app_user_id,
			userPreferences: [],
			users: [],
			selected_brand: { id: "" },
			auth_state: AuthState.SIGNED_IN,
			currentViewMode: getInitialViewMode(), // Profile not available yet, will be set when PROFILE_RESPONSE is received
			loadingCredits: false,
			loadingPermissions: false
		};
	} catch {
		return INIT_STATE;
	}
}

export default function (state: IUserState = getAuthState(), action: ActionType): IUserState {
	switch (action.type) {
		case user.PROFILE_RESPONSE:
			// When profile is loaded, set initial view mode based on subscription type if not already set
			// Otherwise allow any view mode - data will be faded if not subscribed
			const updatedProfile = action.payload;
			let correctedViewMode = state.currentViewMode;

			// Check if there's a saved view mode preference
			const savedMode = storage.getItem("viewMode") as ViewMode | null;
			const hasSavedPreference = savedMode === "supplier" || savedMode === "manufacturer";

			if (updatedProfile) {
				// If subscription expired, force manufacturer view (only override if expired)
				if (isSubscriptionExpired(updatedProfile)) {
					correctedViewMode = "manufacturer";
					storage.setItem("viewMode", correctedViewMode);
				}
				// If no saved preference AND this is the first time profile is loaded, set initial view mode
				// Check if profile was just loaded (state.profile is undefined/null)
				else if (!hasSavedPreference && !state.profile) {
					// Only set default on initial profile load, not on subsequent updates
					correctedViewMode = getDefaultViewMode(updatedProfile);
					storage.setItem("viewMode", correctedViewMode);
				}
				// Otherwise keep current view mode (user preference is respected)
				// This ensures manual toggles are not overridden
			}

			return {
				...state,
				profile: updatedProfile,
				currentViewMode: correctedViewMode
			};
		case user.LOGIN_RESPONSE:
			return action.payload.action_required
				? { ...state, auth_state: AuthState.TWO_FACTOR_AUTH }
				: { ...state, ...getAuthState() };
		case user.USER_PREFERENCE_RESPONSE:
			return { ...state, userPreferences: action.payload };
		case user.LOGOUT_REQUEST:
			return {
				...INIT_STATE,
				isLoggedIn: false
			};
		case user.SELECT_BRAND:
			return {
				...state,
				selected_brand: action.payload
			};
		case user.GET_ALL_COMPANY_USERS:
			return { ...state, users: action.payload };
		case user.SET_VIEW_MODE:
			// Always allow view mode change - no validation blocking
			// Data will be faded on dashboard if not subscribed
			// Persist to localStorage
			storage.setItem("viewMode", action.payload);
			return { ...state, currentViewMode: action.payload };
		case user.GET_USER_CREDITS:
			return {
				...state,
				credits: action.payload,
				loadingCredits: false
			};
		case user.GET_ACCESS_PERMISSIONS:
			return {
				...state,
				permissions: action.payload,
				loadingPermissions: false
			};
		default:
			return state;
	}
}
